/**
 * Authentication context for managing auth state
 */

import { createContext, useContext, useState, ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authService } from '../services/auth.service';
import { userService, type User } from '../services/user.service';
import { queryKeys } from '../lib/query-client';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  idToken: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    displayName: string,
    inviteCode: string
  ) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [idToken, setIdToken] = useState<string | null>(() => {
    // Check if token exists in localStorage on mount
    return localStorage.getItem('idToken');
  });

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return !!localStorage.getItem('idToken');
  });

  const queryClient = useQueryClient();

  // Fetch current user
  const {
    data: user,
    isLoading,
    refetch: refreshUser,
  } = useQuery({
    queryKey: queryKeys.auth.me,
    queryFn: async () => {
      if (!idToken) throw new Error('No token available');
      return await userService.getMe(idToken);
    },
    enabled: isAuthenticated && !!idToken, // Only fetch if authenticated
    retry: false,
  });

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      return await authService.login({ email, password });
    },
    onSuccess: (data) => {
      // Store tokens (use idToken for API Gateway Cognito authorizer)
      localStorage.setItem('idToken', data.idToken);
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);

      // Update auth state
      setIdToken(data.idToken);
      setIsAuthenticated(true);

      // Trigger user fetch
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me });
    },
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: async ({
      email,
      password,
      displayName,
      inviteCode,
    }: {
      email: string;
      password: string;
      displayName: string;
      inviteCode: string;
    }) => {
      return await authService.register({ email, password, displayName, inviteCode });
    },
    // Note: Registration doesn't automatically log in - user needs to verify email
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      try {
        if (idToken) {
          await authService.logout(idToken);
        }
      } catch {
        // Ignore logout errors on server
      }
    },
    onSuccess: () => {
      // Clear tokens
      localStorage.removeItem('idToken');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');

      // Update auth state
      setIdToken(null);
      setIsAuthenticated(false);

      // Clear all cached data
      queryClient.clear();

      // Redirect to login page
      window.location.href = '/login';
    },
  });

  // Context value
  const value: AuthContextType = {
    user: user || null,
    isLoading,
    isAuthenticated,
    idToken,
    login: async (email, password) => {
      await loginMutation.mutateAsync({ email, password });
    },
    register: async (email, password, displayName, inviteCode) => {
      await registerMutation.mutateAsync({ email, password, displayName, inviteCode });
    },
    logout: async () => {
      await logoutMutation.mutateAsync();
    },
    refreshUser: async () => {
      await refreshUser();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
